/**
 * Work out which column holds what: the handle or profile link, niche,
 * email, a date, and notes. Header names count most; when headers are
 * missing or vague, the cell contents decide (a column of links is the
 * handle column whatever it's called).
 */
import { extractHandle } from './handles';

export type ColumnRole = 'handle' | 'niche' | 'email' | 'date' | 'notes';

export const COLUMN_ROLES: ReadonlyArray<{ role: ColumnRole; label: string; required?: boolean }> = [
  { role: 'handle', label: 'Handle or profile link', required: true },
  { role: 'niche', label: 'Niche' },
  { role: 'email', label: 'Email' },
  { role: 'date', label: 'Date' },
  { role: 'notes', label: 'Notes' },
];

export type ColumnMapping = Record<ColumnRole, number | null>;

export const EMPTY_MAPPING: ColumnMapping = { handle: null, niche: null, email: null, date: null, notes: null };

const HEADER_PATTERNS: Record<ColumnRole, RegExp> = {
  handle: /profile|link|url|handle|user\s*name|username|tik\s*tok|insta|\big\b|twitter|\bx\b|youtube|channel|account|creator|social/i,
  niche: /niche|categor|genre|content|type|topic/i,
  email: /e-?mail|mail/i,
  date: /timestamp|date|time|submitted|created/i,
  notes: /note|comment|message|remark|extra|other/i,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_WORDS = /^(nil|null|n\/?a|none|no|nope|-+|—|\.|x|0|na)$/i;

/** "NIL", "N/A", "none", "-" and friends: a form field someone had to fill in but had nothing for. */
export function isEmptyAnswer(value: string | null | undefined): boolean {
  const v = String(value ?? '').trim();
  return v === '' || EMPTY_WORDS.test(v);
}

/** A usable email, or null for blanks, NIL-style answers and junk. */
export function cleanEmail(value: string | null | undefined): string | null {
  const v = String(value ?? '').trim().replace(/^mailto:/i, '');
  if (isEmptyAnswer(v)) return null;
  return EMAIL_RE.test(v) && v.length <= 254 ? v.toLowerCase() : null;
}

export function looksLikeDate(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    /^\d{4}-\d{2}-\d{2}([ T]\d{1,2}:\d{2}(:\d{2})?)?/.test(v) ||
    /^\d{1,2}[/.-]\d{1,2}[/.-]\d{2,4}(\s+\d{1,2}:\d{2}(:\d{2})?(\s*[ap]m)?)?$/i.test(v)
  );
}

/**
 * Whether the first row holds column names rather than data: true unless
 * it already looks like data (a profile link or an email in it).
 */
export function detectHeaderRow(rows: string[][]): boolean {
  const first = rows[0];
  if (!first || first.every((c) => !c.trim())) return false;
  const hasData = first.some((c) => extractHandle(c, null).ok || EMAIL_RE.test(c.trim()) || looksLikeDate(c));
  return !hasData;
}

/** Column names for display: the header text, or "Column A" when there is no header. */
export function columnNames(rows: string[][], hasHeader: boolean): string[] {
  const width = rows.reduce((m, r) => Math.max(m, r.length), 0);
  const header = hasHeader ? rows[0] ?? [] : [];
  return Array.from({ length: width }, (_, i) => {
    const h = (header[i] ?? '').trim();
    return h || `Column ${columnLetter(i)}`;
  });
}

export function columnLetter(i: number): string {
  let s = '';
  let n = i + 1;
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

interface ColumnStats {
  handle: number;
  email: number;
  date: number;
  /** Share of distinct values among non-empty cells (low = category-like). */
  distinct: number;
  /** Average text length. */
  avgLen: number;
  filled: number;
}

function stats(values: string[]): ColumnStats {
  const filled = values.filter((v) => v.trim() !== '');
  const meaningful = filled.filter((v) => !isEmptyAnswer(v));
  const n = Math.max(1, filled.length);
  const m = Math.max(1, meaningful.length);
  return {
    handle: filled.filter((v) => !/^[\d.,\s]+$/.test(v) && extractHandle(v, 'TIKTOK').ok && /[/@.]/.test(v)).length / n,
    email: filled.filter((v) => EMAIL_RE.test(v.trim()) || isEmptyAnswer(v)).length / n,
    date: filled.filter(looksLikeDate).length / n,
    distinct: new Set(meaningful.map((v) => v.trim().toLowerCase())).size / m,
    avgLen: meaningful.reduce((s, v) => s + v.length, 0) / m,
    filled: filled.length,
  };
}

/** Best guess for every role. Each column is used for at most one role. */
export function detectColumns(rows: string[][], hasHeader: boolean): ColumnMapping {
  const names = columnNames(rows, hasHeader);
  const body = (hasHeader ? rows.slice(1) : rows).slice(0, 200);
  const colStats = names.map((_, i) => stats(body.map((r) => r[i] ?? '')));
  const header = (i: number, role: ColumnRole): boolean => hasHeader && HEADER_PATTERNS[role].test(names[i] ?? '');

  const score: Record<ColumnRole, (i: number) => number> = {
    handle: (i) => colStats[i]!.handle * 10 + (header(i, 'handle') ? 4 : 0),
    email: (i) => (colStats[i]!.email >= 0.5 ? colStats[i]!.email * 6 : 0) + (header(i, 'email') ? 8 : 0),
    date: (i) => colStats[i]!.date * 6 + (header(i, 'date') ? 4 : 0),
    niche: (i) => {
      const s = colStats[i]!;
      const categoryLike = s.filled >= 5 && s.distinct <= 0.5 && s.avgLen < 40 && s.handle < 0.2 && s.email < 0.5 && s.date < 0.5;
      return (header(i, 'niche') ? 8 : 0) + (categoryLike ? 2 : 0);
    },
    notes: (i) => (header(i, 'notes') ? 8 : 0),
  };
  const minimum: Record<ColumnRole, number> = { handle: 3, email: 5, date: 4, niche: 8, notes: 8 };

  const mapping: ColumnMapping = { ...EMPTY_MAPPING };
  const used = new Set<number>();
  // Most distinctive content first, so "Email Address" isn't claimed as a handle.
  for (const role of ['handle', 'email', 'date', 'niche', 'notes'] as ColumnRole[]) {
    let best: number | null = null;
    let bestScore = 0;
    names.forEach((_, i) => {
      if (used.has(i)) return;
      const s = score[role](i);
      if (s > bestScore) {
        bestScore = s;
        best = i;
      }
    });
    if (best !== null && bestScore >= minimum[role]) {
      mapping[role] = best;
      used.add(best);
    }
  }
  return mapping;
}
